import { supabase } from './auth';
import { daysUntil } from './races';
import { DailyCheckIn } from './checkIn';
import { StoredRecommendation } from './dailyRecommendation';
import { PlannedWorkout } from './trainingPlan';
import { localDate } from '../utils/date';
import {
  CoachMessage,
  CoachResponse,
  Race,
  RecoveryMetrics,
  SleepMetrics,
  User,
  WorkoutData,
  WorkoutRecommendation,
} from '../types';

/** The full picture the coach reasons over. Built once per dashboard load. */
export interface CoachContext {
  today: string;
  athlete: {
    name: string;
    primaryGoal: User['fitnessGoals']['primary'];
    secondaryGoal?: User['fitnessGoals']['secondary'];
    targetWeeklyWorkouts: number;
    targetIntensity: User['fitnessGoals']['targetIntensity'];
    weeklyTrainingVolumeHours: number;
    activityPreferences: string[];
    weightLbs?: number;
    age?: number;
    maxHrBpm?: number; // user-provided, or age-estimated (flagged)
    maxHrSource?: 'measured' | 'age-estimated';
    availableTrainingDays?: string[];
    typicalSessionMinutes?: number;
    experienceLevel?: string;
    injuryNotes?: string;
    lifeContext?: string;
    thresholdPaceOrPower?: string;
  };
  /** Learned from actual data, not self-reported. */
  observedLast7Days: {
    sessions: number;
    totalHours: number;
    hardSessions: number;
  };
  races: {
    name: string;
    date: string;
    daysUntil: number;
    distanceMiles: number | null;
    priority: 'A' | 'B' | 'C';
    goal: string | null;
  }[];
  /** One-tap subjective state given before today's plan was generated. */
  todayCheckIn?: {
    feeling: 'rough' | 'okay' | 'strong';
    note?: string;
  };
  /** Omitted entirely when today's recovery data could not be read — see
      metricsUnavailable. Never filled with stand-in numbers. */
  todayMetrics?: {
    source: 'oura' | 'garmin-via-terra' | 'apple-health' | 'sample-data';
    readinessScore?: number;
    sleepScore: number;
    sleepDurationHours: number;
    hrvMs: number;
    restingHeartRateBpm: number;
    temperatureDeviationF: number;
  };
  /** Present in place of todayMetrics when there is no recovery data today:
      either nothing is connected, or every connected source failed to read. */
  metricsUnavailable?: 'no-wearable-connected' | 'wearable-read-failed';
  last7DaysTraining: {
    date: string;
    type: string;
    durationMinutes: number;
    distanceMiles?: number;
    averageHeartRate?: number;
    intensity: string;
  }[];
  /** The athlete's external structured plan (Runna etc.), from their iCal
      feed: today's session plus the days ahead. When present, the coach
      treats it as the source of truth for what today is, and adjusts it
      (rather than replacing it) based on recovery. */
  externalPlan?: {
    source: 'calendar-feed';
    today: PlannedWorkout[];
    upcoming: PlannedWorkout[];
  };
  /** What was prescribed recently and how the athlete responded to it. */
  recentPlans?: {
    date: string;
    type: string;
    durationMinutes: number;
    intensity: string;
    rating?: 'up' | 'down';
    completed?: boolean;
  }[];
}

export type MetricsSource =
  | 'oura'
  | 'garmin-via-terra'
  | 'apple-health'
  | 'sample-data'
  | 'no-wearable-connected'
  | 'wearable-read-failed';

export const buildCoachContext = (
  user: User,
  races: Race[],
  workouts: WorkoutData[],
  sleep: SleepMetrics | null,
  recovery: RecoveryMetrics | null,
  metricsSource?: MetricsSource,
  checkIn?: DailyCheckIn | null,
  recentPlans?: StoredRecommendation[],
  plannedWorkouts?: PlannedWorkout[] | null
): CoachContext => {
  const age = user.birthYear ? new Date().getFullYear() - user.birthYear : undefined;
  const maxHr = user.maxHr ?? (age ? 220 - age : undefined);
  const todayIso = localDate();

  // A missing read means missing metrics. Sending plausible stand-in numbers
  // makes the coach cite them as if they were the athlete's own — the plan
  // then reasons about an HRV and sleep score nobody ever recorded.
  const hasMetrics =
    !!sleep &&
    !!recovery &&
    metricsSource !== 'no-wearable-connected' &&
    metricsSource !== 'wearable-read-failed';

  return {
  today: todayIso,
  athlete: {
    name: user.name,
    primaryGoal: user.fitnessGoals.primary,
    secondaryGoal: user.fitnessGoals.secondary,
    targetWeeklyWorkouts: user.fitnessGoals.weeklyWorkouts,
    targetIntensity: user.fitnessGoals.targetIntensity,
    weeklyTrainingVolumeHours: user.weeklyTrainingVolume,
    activityPreferences: user.activityPreferences,
    weightLbs: user.weight,
    age,
    maxHrBpm: maxHr,
    maxHrSource: maxHr ? (user.maxHr ? 'measured' : 'age-estimated') : undefined,
    availableTrainingDays: user.trainingDays?.length ? user.trainingDays : undefined,
    typicalSessionMinutes: user.typicalSessionMinutes,
    experienceLevel: user.experienceLevel,
    injuryNotes: user.injuryNotes,
    lifeContext: user.lifeContext,
    thresholdPaceOrPower: user.thresholdPace,
  },
  observedLast7Days: {
    sessions: workouts.length,
    totalHours: Math.round((workouts.reduce((s, w) => s + w.duration, 0) / 3600) * 10) / 10,
    hardSessions: workouts.filter((w) => w.intensity === 'high').length,
  },
  todayCheckIn: checkIn
    ? { feeling: checkIn.feeling, note: checkIn.note ?? undefined }
    : undefined,
  races: races.map((r) => ({
    name: r.name,
    date: r.race_date,
    daysUntil: daysUntil(r.race_date),
    distanceMiles: r.distance_miles,
    priority: r.priority,
    goal: r.goal,
  })),
  todayMetrics: hasMetrics
    ? {
        source: (metricsSource ?? (recovery.readinessScore !== undefined ? 'oura' : 'sample-data')) as
          Exclude<MetricsSource, 'no-wearable-connected' | 'wearable-read-failed'>,
        readinessScore: recovery.readinessScore,
        sleepScore: sleep.quality,
        sleepDurationHours: Math.round((sleep.duration / 3600) * 10) / 10,
        hrvMs: recovery.hrv,
        restingHeartRateBpm: recovery.restingHeartRate,
        // Wearables report the deviation in °C; the coach speaks imperial.
        temperatureDeviationF: Math.round(recovery.bodyTemperature * 1.8 * 10) / 10,
      }
    : undefined,
  metricsUnavailable: hasMetrics
    ? undefined
    : metricsSource === 'no-wearable-connected'
      ? 'no-wearable-connected'
      : 'wearable-read-failed',
  last7DaysTraining: workouts.map((w) => ({
    date: w.timestamp.split('T')[0],
    type: w.type,
    durationMinutes: Math.round(w.duration / 60),
    distanceMiles: w.distance ? Math.round((w.distance / 1609.34) * 10) / 10 : undefined,
    averageHeartRate: w.averageHeartRate ? Math.round(w.averageHeartRate) : undefined,
    intensity: w.intensity,
  })),
  // The plan-feed function windows by UTC date; re-filter with the
  // athlete's local today so "today" means their today.
  externalPlan: plannedWorkouts?.some((p) => p.date >= todayIso)
    ? {
        source: 'calendar-feed',
        today: plannedWorkouts.filter((p) => p.date === todayIso),
        upcoming: plannedWorkouts.filter((p) => p.date > todayIso),
      }
    : undefined,
  // Today's plan is what we're about to produce — only past days belong.
  recentPlans: recentPlans?.length
    ? recentPlans
        .filter((p) => p.day !== todayIso)
        .map((p) => ({
          date: p.day,
          type: p.recommendation.type,
          durationMinutes: p.recommendation.duration,
          intensity: p.recommendation.intensity,
          rating: p.rating ?? undefined,
          completed: p.completed ?? undefined,
        }))
    : undefined,
  };
};

/** Thrown when the AI coach is unavailable (no API key configured). */
export class CoachUnavailableError extends Error {}

export const askCoach = async (
  context: CoachContext,
  messages: CoachMessage[] = [],
  demo = false
): Promise<CoachResponse> => {
  let token = 'fitscript-demo';
  if (!demo) {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Not signed in');
    token = session.access_token;
  }

  const response = await fetch('/.netlify/functions/ai-coach', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ context, messages }),
  });

  if (response.status === 503) {
    throw new CoachUnavailableError('AI coach is not configured');
  }
  if (!response.ok) {
    const data = await response.json().catch(() => null);
    throw new Error(data?.error || `AI coach error: ${response.status}`);
  }

  return (await response.json()) as CoachResponse;
};

export const coachResponseToRecommendation = (
  coach: CoachResponse
): WorkoutRecommendation => ({
  type: coach.workout.type,
  duration: coach.workout.durationMinutes,
  intensity: coach.workout.intensity,
  details: coach.workout.details,
  alternativeOptions: coach.alternatives,
  reasoning: coach.reasoning,
});
